import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../../../core/services/auth.service';
import { User } from '@core/models/user.model';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, RouterModule, ReactiveFormsModule, TranslateModule],
  template: `
    <div class="login-page">

      <!-- Header -->
      <header class="header">
        <div class="header-container">
          <a routerLink="/" class="logo">
            <img src="assets/images/logo%20kia%20ora.png" alt="Kia Ora" class="logo-img">
          </a>
          <nav class="nav-links">
            <a routerLink="/">{{ 'NAV.HOME' | translate }}</a>
            <a routerLink="/about">{{ 'NAV.ABOUT' | translate }}</a>
            <a routerLink="/contact">{{ 'NAV.CONTACT' | translate }}</a>
            <a *ngIf="user" routerLink="/profile" class="active">Mon Profil</a>
          </nav>
          <div class="header-actions">
            <a routerLink="/booking" class="book-now-btn">
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              {{ 'NAV.BOOK_NOW' | translate }}
            </a>
          </div>
        </div>
      </header>

      <!-- Split layout -->
      <main class="main-content">

        <!-- Left panel — brand -->
        <div class="brand-panel">
          <div class="brand-panel-inner">
            <img src="assets/images/logo%20kia%20ora.png" alt="Kia Ora" class="brand-logo">
            <h2 class="brand-name">KIA ORA</h2>
            <p class="brand-tagline">Une expérience hôtelière d'exception</p>
            <div class="brand-divider"></div>
            <p class="brand-quote">« Le luxe, c'est quand le confort devient art. »</p>
          </div>
          <!-- decorative arc -->
          <div class="deco-arc"></div>
        </div>

        <!-- Right panel — form -->
        <div class="form-panel">
          <div class="login-container">
            <div class="login-header">
              <h1>{{ 'LOGIN.TITLE' | translate }}</h1>
              <p>{{ 'LOGIN.SUBTITLE' | translate }}</p>
            </div>

            <form [formGroup]="loginForm" (ngSubmit)="onSubmit()" class="login-form">
              <div class="form-group" [class.has-error]="isFieldInvalid('usernameOrEmail')">
                <label for="usernameOrEmail">Identifiant</label>
                <div class="input-wrapper">
                  <svg class="input-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                  <input
                    type="text"
                    id="usernameOrEmail"
                    formControlName="usernameOrEmail"
                    [attr.placeholder]="'LOGIN.USER_PLACEHOLDER' | translate"
                    [class.error]="isFieldInvalid('usernameOrEmail')" />
                </div>
                <span class="error-msg" *ngIf="isFieldInvalid('usernameOrEmail')">
                  Entrez votre email ou nom d'utilisateur
                </span>
              </div>

              <div class="form-group" [class.has-error]="isFieldInvalid('password')">
                <label for="password">Mot de passe</label>
                <div class="input-wrapper">
                  <svg class="input-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                  </svg>
                  <input
                    type="password"
                    id="password"
                    formControlName="password"
                    [attr.placeholder]="'LOGIN.PASSWORD_PLACEHOLDER' | translate"
                    [class.error]="isFieldInvalid('password')" />
                </div>
                <span class="error-msg" *ngIf="isFieldInvalid('password')">
                  {{ 'LOGIN.PASSWORD_ERROR' | translate }}
                </span>
              </div>

              <div class="forgot-password">
                <a routerLink="/auth/forgot-password">{{ 'LOGIN.FORGOT' | translate }}</a>
              </div>

              <button type="submit" class="btn-login" [disabled]="loading">
                <span *ngIf="!loading">{{ 'LOGIN.SIGN_IN' | translate }}</span>
                <span *ngIf="loading" class="loader-wrap">
                  <span class="loader"></span>
                  {{ 'LOGIN.SIGNING_IN' | translate }}
                </span>
              </button>

              <div class="error-message" *ngIf="error">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                {{ error }}
              </div>
            </form>

            <div class="login-footer">
              <p>{{ 'LOGIN.NO_ACCOUNT' | translate }} <a routerLink="/auth/register">{{ 'LOGIN.REGISTER' | translate }}</a></p>
            </div>
          </div>
        </div>
      </main>

      <!-- Footer -->
      <footer class="footer">
        <div class="footer-container">
          <div class="footer-grid">
            <div class="footer-col">
              <div class="footer-logo">
                <img src="assets/images/logo%20kia%20ora.png" alt="Kia Ora" class="footer-logo-img">
              </div>
              <p class="footer-desc">{{ 'FOOTER.DESCRIPTION' | translate }}</p>
            </div>
            <div class="footer-col">
              <h4>{{ 'FOOTER.QUICK_LINKS' | translate }}</h4>
              <ul>
                <li><a routerLink="/booking">{{ 'FOOTER.BOOK_NOW' | translate }}</a></li>
                <li><a routerLink="/">{{ 'FOOTER.GALLERY' | translate }}</a></li>
                <li><a routerLink="/contact">{{ 'NAV.CONTACT' | translate }}</a></li>
                <li><a routerLink="/">{{ 'FOOTER.SERVICES' | translate }}</a></li>
              </ul>
            </div>
            <div class="footer-col">
              <h4>{{ 'FOOTER.COMPANY' | translate }}</h4>
              <ul>
                <li><a routerLink="/">{{ 'FOOTER.PRIVACY_POLICY' | translate }}</a></li>
                <li><a routerLink="/">{{ 'FOOTER.REFUND_POLICY' | translate }}</a></li>
                <li><a routerLink="/">{{ 'FOOTER.FAQ' | translate }}</a></li>
                <li><a routerLink="/about">{{ 'NAV.ABOUT' | translate }}</a></li>
              </ul>
            </div>
            <div class="footer-col">
              <h4>{{ 'FOOTER.SOCIAL_MEDIA' | translate }}</h4>
              <div class="social-links">
                <a href="#" aria-label="Facebook">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                  </svg>
                </a>
                <a href="#" aria-label="Instagram">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                  </svg>
                </a>
                <a href="#" aria-label="Twitter">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"></path>
                  </svg>
                </a>
              </div>
            </div>
          </div>
          <div class="footer-bottom">
            <p>&copy; {{ currentYear }} Kia Ora — Tous droits réservés</p>
          </div>
        </div>
      </footer>
    </div>
  `,
  styles: [`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400&family=Jost:wght@300;400;500;600;700&display=swap');

    * { box-sizing: border-box; margin: 0; padding: 0; }

    /* ── Tokens ── */
    :host {
      --burgundy:    #3D1020;
      --burgundy-dk: #2A0916;
      --gold:        #F8B414;
      --gold-dk:     #D99A0A;
      --dark:        #1C2A1C;
      --cream:       #FAF0D7;
      --cream-dk:    #F0E5C5;
      --text-muted:  #7A5A65;
      --border:      #E8D8B4;
      --shadow:      0 4px 24px rgba(61,16,32,.12);
    }

    .login-page {
      min-height: 100vh;
      display: flex;
      flex-direction: column;
      background: var(--cream);
      font-family: 'Jost', sans-serif;
      color: var(--burgundy);
    }

    /* ── Header ── */
    .header {
      background: var(--cream);
      border-bottom: 1px solid var(--border);
      padding: 0 24px;
      position: sticky;
      top: 0;
      z-index: 100;
    }
    .header-container {
      max-width: 1400px;
      margin: 0 auto;
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 68px;
    }
    .logo { display: flex; align-items: center; text-decoration: none; }
    .logo-img { height: 44px; width: auto; }
    .nav-links { display: flex; gap: 28px; }
    .nav-links a {
      text-decoration: none;
      color: var(--burgundy);
      font-size: 14px;
      font-weight: 500;
      transition: color .2s;
    }
    .nav-links a:hover, .nav-links a.active { color: var(--gold-dk); }
    .book-now-btn {
      display: flex;
      align-items: center;
      gap: 7px;
      padding: 10px 20px;
      background: var(--gold);
      color: var(--burgundy);
      text-decoration: none;
      font-size: 13px;
      font-weight: 700;
      letter-spacing: .04em;
      transition: background .2s;
    }
    .book-now-btn:hover { background: var(--gold-dk); }

    /* ── Main split layout ── */
    .main-content {
      flex: 1;
      display: grid;
      grid-template-columns: 420px 1fr;
    }

    /* ── Brand panel ── */
    .brand-panel {
      background: var(--burgundy);
      position: relative;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 60px 40px;
    }
    .brand-panel-inner {
      position: relative;
      z-index: 2;
      text-align: center;
    }
    .brand-logo {
      width: 160px;
      height: auto;
      margin-bottom: 28px;
      background: var(--cream);
      border-radius: 50%;
      padding: 16px;
    }
    .brand-name {
      font-family: 'Cormorant Garamond', serif;
      font-size: 48px;
      font-weight: 700;
      color: var(--cream);
      letter-spacing: .15em;
      line-height: 1;
      margin-bottom: 12px;
    }
    .brand-tagline {
      font-size: 14px;
      color: var(--gold);
      font-weight: 500;
      letter-spacing: .06em;
      text-transform: uppercase;
      margin-bottom: 32px;
    }
    .brand-divider {
      width: 50px;
      height: 2px;
      background: var(--gold);
      margin: 0 auto 28px;
    }
    .brand-quote {
      font-family: 'Cormorant Garamond', serif;
      font-style: italic;
      font-size: 17px;
      color: rgba(250,240,215,.65);
      line-height: 1.7;
      max-width: 260px;
      margin: 0 auto;
    }
    /* decorative arc */
    .deco-arc {
      position: absolute;
      bottom: -120px;
      right: -120px;
      width: 340px;
      height: 340px;
      border-radius: 50%;
      border: 40px solid var(--gold);
      opacity: .12;
      z-index: 1;
    }

    /* ── Form panel ── */
    .form-panel {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 60px 40px;
      background: var(--cream);
    }
    .login-container { width: 100%; max-width: 420px; }

    .login-header {
      text-align: center;
      margin-bottom: 36px;
    }
    .login-header h1 {
      font-family: 'Cormorant Garamond', serif;
      font-size: 34px;
      font-weight: 700;
      color: var(--burgundy);
      margin-bottom: 8px;
    }
    .login-header p {
      color: var(--text-muted);
      font-size: 15px;
    }

    .login-form {
      background: #fff;
      padding: 36px 32px;
      border-radius: 4px;
      box-shadow: var(--shadow);
    }

    .form-group {
      margin-bottom: 22px;
    }
    .form-group label {
      display: block;
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 1.5px;
      text-transform: uppercase;
      color: var(--text-muted);
      margin-bottom: 8px;
    }
    .input-wrapper {
      position: relative;
    }
    .input-icon {
      position: absolute;
      left: 14px;
      top: 50%;
      transform: translateY(-50%);
      color: var(--text-muted);
      pointer-events: none;
    }
    .form-group input {
      width: 100%;
      padding: 13px 16px 13px 42px;
      border: 1px solid var(--border);
      border-radius: 4px;
      font-size: 15px;
      font-family: 'Jost', sans-serif;
      color: var(--burgundy);
      background: var(--cream);
      transition: border-color .2s, background .2s;
    }
    .form-group input::placeholder { color: var(--text-muted); opacity: .7; }
    .form-group input:focus {
      outline: none;
      border-color: var(--gold);
      background: #fff;
    }
    .form-group.has-error input,
    .form-group input.error {
      border-color: var(--burgundy);
    }
    .error-msg {
      display: block;
      color: var(--burgundy);
      font-size: 12px;
      margin-top: 6px;
    }

    .forgot-password {
      text-align: right;
      margin-bottom: 24px;
    }
    .forgot-password a {
      color: var(--text-muted);
      text-decoration: none;
      font-size: 13px;
      font-weight: 500;
      transition: color .2s;
    }
    .forgot-password a:hover { color: var(--gold-dk); }

    .btn-login {
      width: 100%;
      padding: 14px;
      background: var(--gold);
      color: var(--burgundy);
      border: none;
      border-radius: 4px;
      font-size: 15px;
      font-weight: 700;
      font-family: 'Jost', sans-serif;
      letter-spacing: .05em;
      cursor: pointer;
      transition: background .2s, color .2s;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
    }
    .btn-login:hover:not(:disabled) {
      background: var(--burgundy);
      color: var(--cream);
    }
    .btn-login:disabled {
      background: var(--border);
      color: var(--text-muted);
      cursor: not-allowed;
    }
    .loader-wrap { display: flex; align-items: center; gap: 8px; }
    .loader {
      width: 16px;
      height: 16px;
      border: 2px solid rgba(61,16,32,.3);
      border-top-color: var(--burgundy);
      border-radius: 50%;
      animation: spin .8s linear infinite;
    }
    @keyframes spin { to { transform: rotate(360deg); } }

    .error-message {
      margin-top: 16px;
      padding: 12px 16px;
      background: rgba(61,16,32,.07);
      border-left: 3px solid var(--burgundy);
      color: var(--burgundy);
      border-radius: 4px;
      font-size: 14px;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .login-footer {
      margin-top: 24px;
      text-align: center;
      color: var(--text-muted);
      font-size: 14px;
    }
    .login-footer a {
      color: var(--burgundy);
      text-decoration: none;
      font-weight: 700;
    }
    .login-footer a:hover { color: var(--gold-dk); }

    /* ── Footer ── */
    .footer {
      background: var(--dark);
      color: var(--cream);
      padding: 50px 20px 20px;
      font-family: 'Jost', sans-serif;
    }
    .footer-container { max-width: 1200px; margin: 0 auto; }
    .footer-grid {
      display: grid;
      grid-template-columns: 2fr 1fr 1fr 1fr;
      gap: 40px;
      margin-bottom: 36px;
    }
    .footer-logo { margin-bottom: 16px; }
    .footer-logo-img { height: 52px; width: auto; }
    .footer-desc { color: rgba(250,240,215,.55); font-size: 14px; line-height: 1.7; }
    .footer-col h4 {
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 2px;
      text-transform: uppercase;
      color: var(--gold);
      margin-bottom: 18px;
    }
    .footer-col ul { list-style: none; padding: 0; margin: 0; }
    .footer-col ul li { margin-bottom: 10px; }
    .footer-col ul a {
      color: rgba(250,240,215,.55);
      text-decoration: none;
      font-size: 14px;
      transition: color .2s;
    }
    .footer-col ul a:hover { color: var(--gold); }
    .social-links { display: flex; gap: 12px; }
    .social-links a {
      width: 38px;
      height: 38px;
      border-radius: 50%;
      background: rgba(250,240,215,.1);
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--cream);
      transition: background .2s, color .2s;
    }
    .social-links a:hover { background: var(--gold); color: var(--burgundy); }
    .footer-bottom {
      border-top: 1px solid rgba(250,240,215,.1);
      padding-top: 18px;
      text-align: center;
    }
    .footer-bottom p { color: rgba(250,240,215,.35); font-size: 13px; }

    /* ── Responsive ── */
    @media (max-width: 900px) {
      .main-content { grid-template-columns: 1fr; }
      .brand-panel { padding: 48px 24px; min-height: 300px; }
      .brand-name { font-size: 36px; }
      .nav-links { display: none; }
      .footer-grid { grid-template-columns: 1fr 1fr; }
    }
    @media (max-width: 576px) {
      .form-panel { padding: 40px 20px; }
      .login-form { padding: 28px 20px; }
      .footer-grid { grid-template-columns: 1fr; }
    }
  `]
})
export class LoginComponent {
  loginForm: FormGroup;
  loading = false;
  error = '';
  user: User | null = null;
  currentYear = new Date().getFullYear();
  constructor(
    private fb: FormBuilder,
    public authService: AuthService,
    private router: Router,
    private translate: TranslateService
  ) {
    this.translate.setDefaultLang('fr');
    this.translate.use('fr');
    this.loginForm = this.fb.group({
      usernameOrEmail: ['', [Validators.required]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      rememberMe: [false]
    });
    
    this.user = this.authService.getCurrentUser();

    
    if (this.authService.isAuthenticated()) {
      this.router.navigate(['/profile']);
    }
  }

  isFieldInvalid(field: string): boolean {
    const control = this.loginForm.get(field);
    return control ? control.invalid && control.touched : false;
  }

  onSubmit(): void {
    if (this.loginForm.invalid) return;

    this.loading = true;
    this.error = '';

    const { usernameOrEmail, password } = this.loginForm.value;

    this.authService.login({ usernameOrEmail, password }).subscribe({
      next: () => {
        this.router.navigate(['/profile']);
      },
      error: (err: any) => {
        this.error = err.error?.message || err.message || 'Erreur de connexion';
        this.loading = false;
      }
    });
  }
}
